import React, {useState, useEffect} from "react"
import {useDispatch} from "react-redux"
import {useLocation, useNavigate} from "react-router-dom"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import DataRow from "@/components/ui/DataRow"
import Page from "@/layouts/Page"
import {Box, TextField} from "@mui/material"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import FormControl from "@mui/material/FormControl"
import Select, {SelectChangeEvent} from "@mui/material/Select"
import Chip from "@mui/material/Chip"
import OutlinedInput from "@mui/material/OutlinedInput"
import Alert from "@mui/material/Alert"
import {condosRtkApi, updateCondo} from "@/api/condo"
import {
   condoClasses,
   AccessToBeach,
   regions,
   RegionCode,
   zonesByRegion,
} from "@casas-diamante/types"
import {checkNameTaken} from "@/api/utils"
import {useVideoList} from "@/hooks/useVideoList"
import {clearCache, displayZoneLabel} from "@/utils"
import {normalizeInitialVideos} from "@/video"

function UpdateCondo() {
   const navigate = useNavigate()
   const dispatch = useDispatch()
   const location = useLocation()
   const condoId = location.pathname.split("/editCondominio/")[1]
   const [error, setError] = useState<boolean | string>(false)
   const [region, setRegion] = useState<RegionCode>(RegionCode.ACA)
   const [name, setName] = useState("")
   const [prevName, setPrevName] = useState("")
   const [desc, setDesc] = useState("")
   const [address, setAddress] = useState("")
   const [googleMapsLink, setGoogleMapsLink] = useState("")
   const [mapsShareUrl, setMapsShareUrl] = useState("")
   const [zone, setZone] = useState("")
   const [classification, setClassification] = useState("")
   const [accessToBeach, setAccessToBeach] = useState("")
   const [beachClub, setBeachClub] = useState("")
   const [petFriendly, setPetFriendly] = useState("")
   const [vactionRentalsAllowed, setVactionRentalsAllowed] = useState("")
   const [antiquity, setAntiquity] = useState("")
   const [amenities, setAmenities] = useState<string[]>([])
   const [condoSize, setCondoSize] = useState("")
   const [mainImage, setMainImage] = useState("")
   const [images, setImages] = useState<any>()
   const [orderedSections, setOrderedSections] = useState<string[]>([])
   // loading state
   const [loading, setLoading] = useState<boolean>(false)
   const acapulcoSelected = region === RegionCode.ACA
   const {videoInput, setVideoInput, videosList, addVideo, removeVideo, resetVideos} =
      useVideoList({
         onError: setError,
      })

   useEffect(() => {
      setLoading(true)
      const state: any = location.state
      const condo: any = state.condominio
      if (!condo) {
         navigate("/condominios")
         return
      }
      setRegion(condo.region ? condo.region : "")
      setZone(condo.zone ? condo.zone : "")
      setName(condo.name ? condo.name : "")
      setPrevName(condo.name ? condo.name : "")
      setDesc(condo.desc ? condo.desc : "")
      resetVideos(normalizeInitialVideos(condo))
      setAddress(condo.address ? condo.address : "")
      setGoogleMapsLink(condo.googleMaps ? condo.googleMaps : "")
      setMapsShareUrl(condo.mapsShareUrl || "")
      setZone(condo.zone ? condo.zone : "")
      setAccessToBeach(condo.access_to_beach)
      setBeachClub(condo.beach_club === true ? "true" : "false")
      setPetFriendly(condo.pet_friendly === true ? "true" : "false")
      setVactionRentalsAllowed(condo.vacation_rentals_allowed === true ? "true" : "false")
      setAntiquity(condo.antiquity ? condo.antiquity : "")
      setAmenities(condo.amenities ? condo.amenities : [])
      setCondoSize(condo.condo_size ? condo.condo_size : "")
      setMainImage(condo.mainImage)
      setImages(condo.images)
      setClassification(condo.class ? condo.class : "")
      setOrderedSections(condo.orderedSections ? condo.orderedSections : [])
      setLoading(false)
   }, [])

   // in order to update the enviroment state, which is an string array
   const handleChangeAmenities = (event: SelectChangeEvent<typeof amenities>) => {
      const {
         target: {value},
      } = event
      setAmenities(
         // On autofill we get a stringified value.
         typeof value === "string" ? value.split(",") : value,
      )
   }

   const handleSubmit = async () => {
      setError(false)
      setLoading(true)
      // required fields
      if (!name || name === "") {
         setError("Ingresa un nombre")
         setLoading(false)
         return
      }
      if (name !== prevName) {
         if (await checkNameTaken(name, "condominios")) {
            setError("El nombre del condominio ya existe")
            setLoading(false)
            return
         }
      }
      if (!zone || zone === "") {
         setError("Ingresa una zona")
         setLoading(false)
         return
      }
      if (!classification || classification === "") {
         setError("Ingresa una clasificación")
         setLoading(false)
         return
      }
      if (petFriendly === "") {
         setError("Selecciona si es pet friendly")
         setLoading(false)
         return
      }
      if (vactionRentalsAllowed === "") {
         setError("Selecciona si se permiten rentas vacacionales")
         setLoading(false)
         return
      }
      if (condoSize === "") {
         setError("Selecciona el tamaño del condominio")
         setLoading(false)
         return
      }
      if (!mapsShareUrl || mapsShareUrl === "") {
         setError("Ingresa un link de google maps para compartir")
         setLoading(false)
         return
      }
      // transform to booleans
      const beachClubBool = beachClub === "true" ? true : false
      const petFriendlyBool = petFriendly === "true" ? true : false
      const vactionRentalsAllowedBool = vactionRentalsAllowed === "true" ? true : false
      // create condo object
      const condo: any = {
         name,
         region,
         desc,
         videos: videosList.length <= 0 ? null : videosList,
         address,
         googleMaps: googleMapsLink === "" ? null : googleMapsLink,
         zone,
         access_to_beach: accessToBeach,
         beach_club: beachClubBool,
         pet_friendly: petFriendlyBool,
         vacation_rentals_allowed: vactionRentalsAllowedBool,
         antiquity,
         amenities,
         condo_size: condoSize,
         mainImage,
         images,
         orderedSections,
         class: classification,
         mapsShareUrl,
      }

      const condoFields = Object.keys(condo)
      for (const key in condoFields) {
         if (
            (condo[condoFields[key]] === null ||
               condo[condoFields[key]] === undefined ||
               condo[condoFields[key]] === "" ||
               condo[condoFields[key]].length === 0) &&
            condoFields[key] !== "mainImage"
         ) {
            delete condo[condoFields[key]]
         }
      }

      try {
         await updateCondo(condo, condoId)
         dispatch(condosRtkApi.util.invalidateTags([{type: "CondoPriceAnalysis", id: "LIST"}]))
         clearCache(["condos"])
         navigate(`/condominios/${condoId}`)
         setLoading(false)
      } catch (error) {
         setLoading(false)
         console.log(error)
         if (error.response.status === 500) {
            setError(error)
            return
         }
         setError(
            error.response?.data?.errors[0]?.msg
               ? error.response.data.errors[0].msg
               : "Ocurrió un error al actualizar el condominio, revisa la consola para obtener más información.",
         )
      }
   }

   return (
      <Page>
         <ArrowBackIcon
            className="font-bold mb-7 cursor-pointer mt-5"
            onClick={() => navigate(-1)}
         />
         <div className="rounded mb-5 flex flex-col px-4 md:px-[5%] max-w-[1600px] mx-auto gap-4">
            <h3 className="text-center text-2xl text-blue font-bold mt-5 mb-5">Editar: {name}</h3>
            {error && (
               <Alert severity="error" className="text-center text-2xl mb-5 w-[30%] mx-auto">
                  {error}
               </Alert>
            )}
            <h3 className="text-blue">Información general</h3>
            <DataRow>
               <TextField
                  size="small"
                  label="Nombre del condominio *"
                  id="outlined-size-small"
                  fullWidth
                  sx={{margin: "0px 2%"}}
                  value={name ? name : ""}
                  onChange={(e) => setName(e.target.value)}
               />
               <FormControl size="small" fullWidth sx={{margin: "0px 2%"}}>
                  <InputLabel>Región *</InputLabel>
                  <Select
                     label="Región *"
                     value={region}
                     onChange={(e) => setRegion(e.target.value as RegionCode)}
                     disabled
                  >
                     {regions.map((region) => (
                        <MenuItem key={region.code} value={region.code}>
                           {region.text}
                        </MenuItem>
                     ))}
                  </Select>
               </FormControl>
               <FormControl size="small" fullWidth sx={{margin: "0px 2%"}}>
                  <InputLabel id="demo-controlled-open-select-label">Zona</InputLabel>
                  <Select
                     displayEmpty
                     labelId="demo-controlled-open-select-label"
                     id="demo-controlled-open-select"
                     label="Zona"
                     value={zone}
                     onChange={(e) => setZone(e.target.value)}
                  >
                     {zonesByRegion[region]?.map((z) => (
                        <MenuItem key={z} value={z}>
                           {displayZoneLabel(z)}
                        </MenuItem>
                     ))}
                  </Select>
               </FormControl>
            </DataRow>
            <DataRow>
               <TextField
                  size="small"
                  label="Dirección"
                  id="outlined-size-small"
                  fullWidth
                  sx={{margin: "0px 2%"}}
                  value={address ? address : ""}
                  onChange={(e) => setAddress(e.target.value)}
               />
               {/* clasificación */}
               <FormControl size="small" fullWidth sx={{margin: "0px 2%"}}>
                  <InputLabel id="demo-controlled-open-select-label">Clasificación *</InputLabel>
                  <Select
                     displayEmpty
                     labelId="demo-controlled-open-select-label"
                     id="demo-controlled-open-select"
                     label="Clasificación *"
                     value={classification ? classification : ""}
                     onChange={(e) => setClassification(e.target.value)}
                  >
                     {condoClasses.map((c) => (
                        <MenuItem key={c} value={c}>
                           {c}
                        </MenuItem>
                     ))}
                  </Select>
               </FormControl>
            </DataRow>
            {/* googleMaps embed url */}
            <TextField
               size="small"
               label="Google Maps URL para EMBED"
               id="outlined-size-small"
               fullWidth
               sx={{margin: "0px 2%"}}
               value={googleMapsLink ? googleMapsLink : ""}
               onChange={(e) => setGoogleMapsLink(e.target.value)}
            />
            {/* googleMaps share url */}
            <TextField
               size="small"
               label="Google Maps URL para compartir"
               id="outlined-size-small"
               fullWidth
               sx={{margin: "0px 2%"}}
               value={mapsShareUrl}
               onChange={(e) => setMapsShareUrl(e.target.value)}
            />
            <DataRow>
               <FormControl size="small" fullWidth sx={{margin: "0px 2%"}}>
                  <InputLabel id="demo-controlled-open-select-label">Acceso a la playa</InputLabel>
                  <Select
                     displayEmpty
                     labelId="demo-controlled-open-select-label"
                     id="demo-controlled-open-select"
                     label="Acceso a la playa"
                     value={accessToBeach}
                     disabled={!acapulcoSelected}
                     onChange={(e) => {
                        const value = e.target.value
                        setAccessToBeach(value)
                        if (value === Object.values(AccessToBeach)[1]) {
                           // si el valor es el de club de playa, setear club de playa a verdadero
                           setBeachClub("true")
                        }
                     }}
                  >
                     {Object.values(AccessToBeach).map((a: AccessToBeach) => (
                        <MenuItem key={a} value={a}>
                           {a}
                        </MenuItem>
                     ))}
                  </Select>
               </FormControl>
               <FormControl size="small" fullWidth sx={{margin: "0px 2%"}}>
                  <InputLabel id="demo-controlled-open-select-label">Club de playa</InputLabel>
                  <Select
                     displayEmpty
                     labelId="demo-controlled-open-select-label"
                     id="demo-controlled-open-select"
                     label="Club de Playa"
                     value={beachClub}
                     onChange={(e) => setBeachClub(e.target.value)}
                     disabled={!acapulcoSelected}
                  >
                     <MenuItem value={"true"}>Si</MenuItem>
                     <MenuItem value={"false"}>No</MenuItem>
                  </Select>
               </FormControl>
               {/* vaction_rentals_allowed */}
               <FormControl size="small" fullWidth sx={{margin: "0px 2%"}}>
                  <InputLabel id="demo-controlled-open-select-label">
                     Rentas vacacionales permitidas
                  </InputLabel>
                  <Select
                     displayEmpty
                     labelId="demo-controlled-open-select-label"
                     id="demo-controlled-open-select"
                     label="Rentas vacacionales permitidas"
                     value={vactionRentalsAllowed}
                     onChange={(e) => setVactionRentalsAllowed(e.target.value)}
                  >
                     <MenuItem value={"true"}>Si</MenuItem>
                     <MenuItem value={"false"}>No</MenuItem>
                  </Select>
               </FormControl>
            </DataRow>
            <DataRow>
               {/* pet_friendly */}
               <FormControl size="small" fullWidth sx={{margin: "0px 2%"}}>
                  <InputLabel id="demo-controlled-open-select-label">Pet friendly</InputLabel>
                  <Select
                     displayEmpty
                     labelId="demo-controlled-open-select-label"
                     id="demo-controlled-open-select"
                     label="Pet friendly"
                     value={petFriendly}
                     onChange={(e) => setPetFriendly(e.target.value)}
                  >
                     <MenuItem value={"true"}>Si</MenuItem>
                     <MenuItem value={"false"}>No</MenuItem>
                  </Select>
               </FormControl>
               {/* condo antiquity */}
               <FormControl size="small" fullWidth sx={{margin: "0px 2%"}}>
                  <InputLabel id="demo-controlled-open-select-label">
                     Antigüedad del condominio
                  </InputLabel>
                  <Select
                     displayEmpty
                     labelId="demo-controlled-open-select-label"
                     id="demo-controlled-open-select"
                     label="Antiguedad del condominio"
                     value={antiquity ? antiquity : ""}
                     onChange={(e) => setAntiquity(e.target.value)}
                  >
                     <MenuItem value={"En construcción"}>En construcción</MenuItem>
                     <MenuItem value={"A estrenar"}>A estrenar</MenuItem>
                     <MenuItem value={"Entre 1 y 5 años"}>Entre 1 y 5 años</MenuItem>
                     <MenuItem value={"Entre 5 y 10 años"}>Entre 5 y 10 años</MenuItem>
                     <MenuItem value={"Entre 10 y 20 años"}>Entre 10 y 20 años</MenuItem>
                     <MenuItem value={"Entre 20 y 40 años"}>Entre 20 y 40 años</MenuItem>
                     <MenuItem value={"40 años o más"}>40 años o más</MenuItem>
                  </Select>
               </FormControl>
               {/* condo_size */}
               <FormControl size="small" fullWidth sx={{margin: "0px 2%"}}>
                  <InputLabel id="demo-controlled-open-select-label">
                     Tamaño del condominio
                  </InputLabel>
                  <Select
                     displayEmpty
                     labelId="demo-controlled-open-select-label"
                     id="demo-controlled-open-select"
                     label="Tamaño del condominio"
                     value={condoSize ? condoSize : ""}
                     onChange={(e) => setCondoSize(e.target.value)}
                  >
                     <MenuItem value={"Chico < 40 propiedades"}>
                        Chico {"<"} 40 propiedades
                     </MenuItem>
                     <MenuItem value={"Mediano entre 40 y 80 propiedades"}>
                        Mediano entre 40 y 80 propiedades
                     </MenuItem>
                     <MenuItem value={"Grande +100 propiedades"}>Grande +100 propiedades</MenuItem>
                  </Select>
               </FormControl>
            </DataRow>
            {/* amenities */}
            <FormControl size="small" sx={{margin: "0px 2%", width: "100%"}}>
               <InputLabel id="demo-multiple-chip-label">Amenidades</InputLabel>
               <Select
                  labelId="demo-multiple-chip-label"
                  id="demo-multiple-chip"
                  multiple
                  value={amenities}
                  onChange={handleChangeAmenities}
                  input={<OutlinedInput id="select-multiple-chip" label="Amenidades" />}
                  renderValue={(selected) => (
                     <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                        {selected.map((value) => (
                           <Chip key={value} label={value} />
                        ))}
                     </Box>
                  )}
               >
                  <MenuItem value={"Albercas"}>Albercas</MenuItem>
                  <MenuItem value={"Cancha de Tenis"}>Cancha de Tenis</MenuItem>
                  <MenuItem value={"Cancha de Pádel"}>Cancha de Pádel</MenuItem>
                  <MenuItem value={"Gimnasio"}>Gimnasio</MenuItem>
                  <MenuItem value={"Salón multi usos"}>Salón multi usos</MenuItem>
                  <MenuItem value={"Ludoteca"}>Ludoteca</MenuItem>
                  <MenuItem value={"Spa"}>Spa</MenuItem>
                  <MenuItem value={"Estética"}>Estética</MenuItem>
                  <MenuItem value={"Restaurante"}>Restaurante</MenuItem>
                  <MenuItem value={"Cine"}>Cine</MenuItem>
                  <MenuItem value={"Salón de Jóvenes"}>Salón de Jóvenes</MenuItem>
                  <MenuItem value={"Snack Bar"}>Snack Bar</MenuItem>
                  <MenuItem value={"Elevador"}>Elevador</MenuItem>
                  <MenuItem value={"Seguridad 24/7"}>Seguridad 24/7</MenuItem>
                  <MenuItem value={"Acceso a personas con movilidad reducida"}>
                     Acceso a personas con movilidad reducida
                  </MenuItem>
                  <MenuItem value={"Estacionamiento de visitas"}>
                     Estacionamiento de visitas
                  </MenuItem>
                  <MenuItem value={"Roof Garden"}>Roof Garden</MenuItem>
                  <MenuItem value={"Clínica"}>Clínica</MenuItem>
               </Select>
            </FormControl>
            <h3 className="text-blue">Descripción del condominio</h3>
            {/* description */}
            <TextField
               size="small"
               label="Descripción detallada *"
               id="outlined-size-small"
               fullWidth
               multiline
               sx={{margin: "0px 2%"}}
               value={desc ? desc : ""}
               onChange={(e) => setDesc(e.target.value)}
            />
            <h3 className="text-blue">Videos</h3>
            <div className="w-full flex flex-col gap-4">
               <div className="w-full flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <TextField
                     size="small"
                     label="Links de videos"
                     id="outlined-size-small"
                     fullWidth
                     sx={{mx: {xs: 0, md: "2%"}, mt: {xs: 1, md: 0}}}
                     value={videoInput}
                     onChange={(e) => setVideoInput(e.target.value)}
                  />
                  <AddIcon
                     className="text-blue-600 self-start sm:self-center"
                     onClick={addVideo}
                  />
               </div>
               {videosList.length > 0 &&
                  videosList.map((video, index) => (
                     <div
                        key={video}
                        className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 my-4 border rounded p-5"
                     >
                        <p className="w-[100%] overflow-x-scroll overflow-y-hidden">{video}</p>
                        <DeleteIcon
                           className="text-red-600 self-start sm:self-center"
                           onClick={() => removeVideo(index)}
                        />
                     </div>
                  ))}
            </div>
            {/* add button */}
            {!loading && (
               <button
                  className="rounded bg-blue-600 p-2 mb-5 text-white font-bold w-[200px] self-center"
                  onClick={handleSubmit}
               >
                  Guardar Cambios
               </button>
            )}
            {loading && (
               <button
                  className="rounded bg-blue-600 p-2 mb-5 text-white font-bold w-[200px] self-center"
                  disabled
               >
                  Cargando...
               </button>
            )}
         </div>
      </Page>
   )
}

export default UpdateCondo
